import { AdUserPayload, AdUserTokenPayload } from 'drawbridge.shared/models/payloads/adUserPayload';
import { callApi, callUserAuthApi } from '@utility/api';
import { createAction, createReducer } from '@reduxjs/toolkit';

import { HttpMethod } from 'drawbridge.shared/constants/httpMethod';
import { SecurityState } from '@state/securityState';
import { createApiThunk } from '@reduxHelpers';
import { v4 as uuidv4 } from 'uuid';

export const login = createApiThunk<AdUserTokenPayload, { username: string, password: string }>('/security/login', async (args, thunkApi) => {
    const response = await callApi<AdUserTokenPayload>('/security/login', HttpMethod.POST, {
        username: args.username,
        password: args.password,
    });

    return response.data;
});

export const logout = createApiThunk<void, void>('security/logout', async (arg, thunkAPI) => {
    await callUserAuthApi('/security/logout', HttpMethod.POST);
});

const refreshTokenPromise = createAction<Promise<AdUserTokenPayload>>('/security/refreshTokenPromise');
const refreshComplete = createAction<AdUserTokenPayload>('/security/refreshComplete');
export const refreshToken = createApiThunk<AdUserTokenPayload, boolean>('/security/refreshToken', (autoLogOut, thunkApi) => {
    const { refreshToken, deviceUUID } = thunkApi.getState().security;

    const promise = callApi<AdUserTokenPayload>('/security/refresh', HttpMethod.POST, {
        refreshToken: refreshToken,
        deviceUUID: deviceUUID,
    }).then((response) => {
        thunkApi.dispatch(refreshComplete(response.data));

        return response.data;
    }).catch((err) => {
        if (autoLogOut) {
            thunkApi.dispatch(logout());
        }

        throw (err);
    });

    thunkApi.dispatch(refreshTokenPromise(promise));

    return promise;
});

export const authenticate = createApiThunk<AdUserPayload, void>('/security/authenticate', async (_noInput, thunkApi) => {
    const { data } = await callUserAuthApi<AdUserPayload>('/security/authenticate');

    return data;
});

const initialState: SecurityState = {
    hasError: false,
    isAuthenticated: false,
    isAuthenticating: false,
    isLoggingOut: false,
    isRefreshingToken: false,
    message: undefined,
    refreshPromise: undefined,
    adUser: undefined,
    accessToken: undefined,
    refreshToken: undefined,
    deviceUUID: uuidv4(),
};

export const securityReducer = createReducer(initialState, (builder) => {
    builder
        .addCase(login.pending, (state, action) => {
            state.isAuthenticating = true;
            // state.message = '';
        })
        .addCase(login.fulfilled, (state, action) => {
            state.isAuthenticating = false;
            state.hasError = false;
            state.isAuthenticated = true;
            state.isRefreshingToken = false;
            state.adUser = action.payload.adUser;
            state.refreshToken = action.payload.refreshToken;
            state.accessToken = action.payload.accessToken;
        })
        .addCase(login.rejected, (state, action) => {
            state.isAuthenticating = false;
            state.isAuthenticated = false;
            state.hasError = true;
            state.isRefreshingToken = false;
            // state.message = action.payload?.response.data.message ?? action?.error?.message;
            state.adUser = undefined;
            state.accessToken = undefined;
            state.refreshPromise = undefined;
            state.refreshToken = undefined;
        })
        .addCase(refreshTokenPromise, (state, action) => {
            state.refreshPromise = action.payload;
            state.isRefreshingToken = true;
        })
        .addCase(refreshToken.pending, (state, action) => {
            state.isRefreshingToken = true;
            // state.refreshPromise = action.payload;
        })
        .addCase(refreshToken.fulfilled, (state, action) => {
            // state.hasError = false;
            // state.isAuthenticated = true;
            // state.isRefreshingToken = false;
            // state.message = '';
            // state.refreshPromise = undefined;
            // state.adUser = action.payload.adUser;
            // state.accessToken = action.payload.accessToken;
            // state.refreshToken = action.payload.refreshToken;
            // state.isAuthenticating = false;
        })
        .addCase(refreshComplete, (state, action) => {
            state.isRefreshingToken = false;
            state.refreshPromise = undefined;
            state.adUser = action.payload.adUser;
            state.accessToken = action.payload.accessToken;
            state.refreshToken = action.payload.refreshToken;
            state.isAuthenticating = false;
            state.isAuthenticated = true;
        })
        .addCase(refreshToken.rejected, (state, action) => {
            if (action.payload?.noResponse === true) {
                // We didn't actually get a response from our API.
                // It turns out its super annoying to log out people if they just can't hit the server, lets let this pass...
                return;
            }
            state.hasError = true;
            state.isAuthenticated = false;
            state.isRefreshingToken = false;
            // state.message = action.payload?.response.data.message ?? action.error.message;
            state.refreshPromise = undefined;
            state.adUser = undefined;
            state.refreshToken = undefined;
            state.accessToken = undefined;
        })
        .addCase(logout.pending, (state, action) => {
            state.isLoggingOut = true;
        })
        .addCase(logout.fulfilled, (state, action) => {
            state.accessToken = undefined;
            state.adUser = undefined;
            state.isAuthenticated = false;
            state.isAuthenticating = false;
            state.isLoggingOut = false;
            state.isRefreshingToken = false;
            state.message = undefined;
            state.refreshPromise = undefined;
            state.refreshToken = undefined;
        })
        .addCase(logout.rejected, (state, action) => {
            // Even if the logout call to the server is rejected,
            // we still want to effectively log out in the client!
            state.accessToken = undefined;
            state.adUser = undefined;
            state.isAuthenticated = false;
            state.isAuthenticating = false;
            state.isLoggingOut = false;
            state.isRefreshingToken = false;
            state.message = undefined;
            state.refreshPromise = undefined;
            state.refreshToken = undefined;
        })
        .addCase(authenticate.pending, (state, action) => {
            state.isAuthenticating = true;
        })
        .addCase(authenticate.fulfilled, (state, action) => {
            state.isAuthenticating = false;
            state.isAuthenticated = true;
            state.adUser = action.payload.adUser;
        })
        .addCase(authenticate.rejected, (state, action) => {
            if (action.payload?.noResponse === true) {
                // We didn't actually get a response from our API.
                // It turns out its super annoying to log out people if they just can't hit the server, lets let this pass...
                return;
            }
            state.isAuthenticating = false;
            state.isAuthenticated = false;
            state.adUser = undefined;
            state.refreshToken = undefined;
            state.accessToken = undefined;
            state.hasError = true;
        });
});